import { useState, type FormEvent } from "react";
import { z } from "zod";
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  PhoneCall,
  Plus,
  ShieldAlert,
  Stethoscope,
  X,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { normalizeUrgency, isAtLeast, URGENCY_META } from "@/lib/urgency";
import { Card, CardBody, CardHeader, CardSection } from "@/components/ui/Card";
import { Button, LinkButton } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input, Select, Textarea } from "@/components/ui/Field";
import { Alert, UrgencyPanel } from "@/components/ui/Feedback";
import { RichText } from "@/components/RichText";
import { useServiceLink } from "@/lib/serviceLink";
import { cn } from "@/lib/cn";

/**
 * Symptom triage.
 *
 * Public — no login required — and unchanged on the server: it drives the same
 * two-stage flow the old page did. /api/triage/questions asks the model for
 * complaint-specific red flags (falling back to a static set when the model is
 * unavailable), then /api/triage/analyze scores the answers.
 */

const questionSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().default(""),
  follow_up: z.string().optional(),
});

const questionsSchema = z.looseObject({
  questions: z.array(questionSchema).default([]),
  additional_symptoms: z.array(questionSchema).default([]),
  source: z.string().optional(),
});

/**
 * `analysis_detail` is a structured object, not a string — the rule engine and
 * the AI path both emit {summary, observed, actions, watch_for, source}.
 * `observed` and `watch_for` may arrive as either a string or a list depending
 * on which path produced them, so both are accepted.
 */
const textOrList = z
  .union([z.string(), z.array(z.string())])
  .nullable()
  .default(null)
  .transform((value) =>
    value == null ? [] : Array.isArray(value) ? value : [value],
  );

const analysisDetailSchema = z.looseObject({
  summary: z.string().nullable().default(null),
  observed: textOrList,
  actions: textOrList,
  watch_for: textOrList,
  source: z.string().nullable().default(null),
});

const analysisSchema = z.looseObject({
  ticket: z.string(),
  urgency_level: z.string(),
  urgency_score: z.number(),
  recommendation: z.string(),
  analysis_detail: analysisDetailSchema.nullable().default(null),
});

type Question = z.infer<typeof questionSchema>;
type Analysis = z.infer<typeof analysisSchema>;

type Stage = "complaint" | "screening" | "vitals" | "result";

const CONSCIOUSNESS = [
  { value: "sadar", label: "Sadar penuh" },
  { value: "mengantuk", label: "Mengantuk / sulit fokus" },
  { value: "bingung", label: "Bingung atau linglung" },
  { value: "tidak sadar", label: "Tidak sadar" },
];

const BP_STATES = [
  { value: "normal", label: "Normal / tidak tahu" },
  { value: "tinggi", label: "Tinggi" },
  { value: "rendah", label: "Rendah" },
];

export function TriageScreen() {
  const serviceLink = useServiceLink();
  const [stage, setStage] = useState<Stage>("complaint");

  const [name, setName] = useState("");
  const [nik, setNik] = useState("");
  const [phone, setPhone] = useState("");
  const [complaint, setComplaint] = useState("");

  const [critical, setCritical] = useState<Question[]>([]);
  const [additional, setAdditional] = useState<Question[]>([]);
  const [source, setSource] = useState<string | undefined>();
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [manual, setManual] = useState<string[]>([]);
  const [manualDraft, setManualDraft] = useState("");

  const [temp, setTemp] = useState("36.5");
  const [bp, setBp] = useState("normal");
  const [consciousness, setConsciousness] = useState("sadar");

  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadQuestions(event: FormEvent) {
    event.preventDefault();
    if (complaint.trim().length < 3) {
      setError("Jelaskan keluhan utama Anda minimal 3 karakter.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = await api.post("/api/triage/questions", questionsSchema, {
        primary_symptom: complaint.trim(),
      });
      setCritical(data.questions);
      setAdditional(data.additional_symptoms);
      setSource(data.source);
      setStage("screening");
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Gagal menyusun pertanyaan skrining.");
    } finally {
      setBusy(false);
    }
  }

  async function analyse() {
    setBusy(true);
    setError(null);
    try {
      const criticalIds = critical.map((item) => item.id);
      const data = await api.post("/api/triage/analyze", analysisSchema, {
        name: name.trim(),
        nik: nik.trim(),
        phone: phone.trim(),
        primary_symptom: complaint.trim(),
        vital_temp: temp,
        vital_bp: bp,
        vital_consciousness: consciousness,
        // Only red flags belong in `symptoms`; the follow-up answers travel
        // separately so the scorer does not double-count them.
        symptoms: [...checked].filter((id) => criticalIds.includes(id)),
        additional_answers: Object.fromEntries(
          Object.entries(answers).filter(([id]) => !criticalIds.includes(id)),
        ),
        manual_symptoms: manual,
      });
      setAnalysis(data);
      setStage("result");
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Analisis triase gagal.");
    } finally {
      setBusy(false);
    }
  }

  function toggle(id: string) {
    const next = new Set(checked);
    if (next.has(id)) {
      next.delete(id);
      const rest = { ...answers };
      delete rest[id];
      setAnswers(rest);
    } else {
      next.add(id);
    }
    setChecked(next);
  }

  const urgency = analysis ? normalizeUrgency(analysis.urgency_level) : null;
  const hasRedFlag = critical.some((item) => checked.has(item.id));

  return (
    // A div, not <main>: this screen renders inside both the public shell and
    // the patient shell, and each of those already provides the landmark.
    // max-w-5xl, not 3xl: the screening lists are two columns now, and the
    // narrower column left a wide empty margin on either side of the card.
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
      <header className="mb-6">
        <p className="eyebrow mb-1.5">Skrining awal</p>
        <h1 className="text-3xl">Triase Gejala</h1>
        <p className="text-content-muted mt-2 max-w-xl text-sm">
          Menentukan seberapa cepat Anda perlu diperiksa. Bukan diagnosis, dan tidak menggantikan
          penilaian tenaga medis.
        </p>
      </header>

      {error && (
        <Alert tone="danger" title="Terjadi kesalahan" className="mb-4">
          {error}
        </Alert>
      )}

      {stage === "complaint" && (
        <Card>
          <CardHeader
            eyebrow="Langkah 1"
            title="Keluhan utama"
            description="Pertanyaan skrining disusun berdasarkan keluhan yang Anda tulis."
          />
          <CardBody>
            <form onSubmit={loadQuestions} className="space-y-4">
              <Textarea
                label="Apa keluhan utama Anda?"
                placeholder="Contoh: nyeri dada sejak dua jam lalu disertai keringat dingin"
                value={complaint}
                onChange={(event) => setComplaint(event.target.value)}
                required
              />
              <div className="grid gap-4 sm:grid-cols-3">
                <Input
                  label="Nama"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  hint="Opsional"
                />
                <Input
                  label="NIK"
                  value={nik}
                  onChange={(event) => setNik(event.target.value)}
                  hint="Opsional"
                />
                <Input
                  label="Telepon"
                  type="tel"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  hint="Opsional"
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                loading={busy}
                icon={<ArrowRight className="size-4" />}
              >
                Lanjutkan
              </Button>
            </form>
          </CardBody>
        </Card>
      )}

      {stage === "screening" && (
        <div className="space-y-4">
          {source === "fallback" && (
            <Alert tone="warning">
              Pertanyaan disusun dari daftar standar karena layanan AI sedang tidak tersedia.
              Penilaian tetap berjalan.
            </Alert>
          )}

          {/* One card, three sections. Three stacked cards each wrapping a
              list of bordered rows put a box inside a box inside a box, and
              made the screening twice as long as it needed to be. */}
          <Card className="overflow-hidden">
            <CardSection
              eyebrow="Langkah 2"
              title="Tanda bahaya"
              description="Centang yang Anda alami saat ini."
              className="border-t-0"
            >
              {/* Two columns: each item is two short lines, and one per row
                  left most of the card empty to its right. */}
              <div className="grid gap-2.5 md:grid-cols-2">
                {critical.map((item) => (
                  <label
                    key={item.id}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3",
                      checked.has(item.id)
                        ? "border-critical-500/40 bg-critical-tint"
                        : "border-hairline hover:border-hairline-strong",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked.has(item.id)}
                      onChange={() => toggle(item.id)}
                      className="accent-critical-500 mt-0.5 size-4 shrink-0"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold">{item.title}</span>
                      <span className="text-content-muted block text-xs">{item.description}</span>
                    </span>
                  </label>
                ))}
              </div>

              {hasRedFlag && (
                <Alert tone="danger" title="Tanda bahaya terdeteksi" className="mt-2.5">
                  Jangan menunggu hasil analisis. Hubungi <b>119</b> atau menuju IGD terdekat
                  sekarang.
                </Alert>
              )}
            </CardSection>

            {additional.length > 0 && (
              <CardSection eyebrow="Langkah 3" title="Pertanyaan lanjutan">
                <div className="grid gap-2.5 md:grid-cols-2">
                  {additional.map((item) => (
                    <div
                      key={item.id}
                      className={cn(
                        "rounded-xl border px-4 py-3",
                        checked.has(item.id) ? "border-accent bg-accent/5" : "border-hairline",
                      )}
                    >
                      <label className="flex cursor-pointer items-start gap-3">
                        <input
                          type="checkbox"
                          checked={checked.has(item.id)}
                          onChange={() => toggle(item.id)}
                          className="accent-accent mt-0.5 size-4 shrink-0"
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold">{item.title}</span>
                          <span className="text-content-muted block text-xs">
                            {item.description}
                          </span>
                        </span>
                      </label>

                      {checked.has(item.id) && item.follow_up && (
                        <Input
                          className="mt-2.5"
                          label={item.follow_up}
                          value={answers[item.id] ?? ""}
                          onChange={(event) =>
                            setAnswers({ ...answers, [item.id]: event.target.value })
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>
              </CardSection>
            )}

            <CardSection eyebrow="Opsional" title="Gejala lain" className="space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Tuliskan gejala lain yang Anda rasakan"
                  value={manualDraft}
                  onChange={(event) => setManualDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      const value = manualDraft.trim();
                      if (value && manual.length < 5) setManual([...manual, value]);
                      setManualDraft("");
                    }
                  }}
                  className="flex-1"
                />
                <Button
                  icon={<Plus className="size-4" />}
                  disabled={!manualDraft.trim() || manual.length >= 5}
                  onClick={() => {
                    setManual([...manual, manualDraft.trim()]);
                    setManualDraft("");
                  }}
                >
                  Tambah
                </Button>
              </div>
              {manual.length > 0 && (
                <ul className="flex flex-wrap gap-2">
                  {manual.map((item, index) => (
                    <li key={index}>
                      <button
                        type="button"
                        onClick={() => setManual(manual.filter((_, i) => i !== index))}
                        className="bg-surface-sunken ring-hairline-strong inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ring-1"
                      >
                        {item}
                        <X className="size-3" />
                        <span className="sr-only">Hapus</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-content-subtle text-xs">Maksimal 5 gejala tambahan.</p>
            </CardSection>
          </Card>

          <div className="flex justify-between">
            <Button icon={<ArrowLeft className="size-4" />} onClick={() => setStage("complaint")}>
              Kembali
            </Button>
            <Button
              variant="primary"
              icon={<ArrowRight className="size-4" />}
              onClick={() => setStage("vitals")}
            >
              Lanjut ke tanda vital
            </Button>
          </div>
        </div>
      )}

      {stage === "vitals" && (
        <Card>
          <CardHeader
            eyebrow="Langkah 4"
            title="Kondisi saat ini"
            description="Isi seadanya. Pilih 'tidak tahu' bila belum diukur."
          />
          <CardBody className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Input
                label="Suhu tubuh (°C)"
                type="number"
                step="0.1"
                min="30"
                max="45"
                value={temp}
                onChange={(event) => setTemp(event.target.value)}
                hint="Gunakan termometer."
              />
              <Select
                label="Tekanan darah"
                value={bp}
                onChange={(event) => setBp(event.target.value)}
              >
                {BP_STATES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Select>
              <Select
                label="Kesadaran"
                value={consciousness}
                onChange={(event) => setConsciousness(event.target.value)}
              >
                {CONSCIOUSNESS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex justify-between">
              <Button icon={<ArrowLeft className="size-4" />} onClick={() => setStage("screening")}>
                Kembali
              </Button>
              <Button
                variant="primary"
                loading={busy}
                icon={<Activity className="size-4" />}
                onClick={analyse}
              >
                Analisis triase
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {stage === "result" && analysis && urgency && (
        <div className="space-y-4">
          <UrgencyPanel level={urgency}>
            <p>{analysis.recommendation}</p>
            {isAtLeast(urgency, "critical") && (
              <a
                href="tel:119"
                className="bg-critical-500 mt-3 inline-flex h-10 items-center gap-2 rounded-(--radius-control) px-5 text-sm font-bold text-white"
              >
                <PhoneCall className="size-4" />
                Hubungi 119
              </a>
            )}
          </UrgencyPanel>

          <Card>
            <CardHeader
              eyebrow="Referensi"
              title="Rincian penilaian"
              action={<Badge tone={urgency}>Skor {analysis.urgency_score}</Badge>}
            />
            <CardBody className="space-y-4">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge>Tiket {analysis.ticket}</Badge>
                <Badge tone={urgency}>{URGENCY_META[urgency].label}</Badge>
                {analysis.analysis_detail?.source === "clinical_rules" && (
                  <Badge tone="neutral">Aturan klinis</Badge>
                )}
              </div>

              {analysis.analysis_detail?.summary && (
                <RichText
                  text={analysis.analysis_detail.summary}
                  className="text-sm leading-relaxed"
                />
              )}

              {(
                [
                  ["Yang Anda laporkan", analysis.analysis_detail?.observed ?? []],
                  ["Langkah yang disarankan", analysis.analysis_detail?.actions ?? []],
                  ["Waspadai", analysis.analysis_detail?.watch_for ?? []],
                ] satisfies [string, string[]][]
              ).map(([title, items]) =>
                items.length > 0 ? (
                  <div key={title}>
                    <p className="eyebrow mb-1.5">{title}</p>
                    <ul className="list-disc space-y-1 pl-5 text-sm">
                      {items.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null,
              )}

              <a
                href={`/triage/pdf/${analysis.ticket}`}
                className="text-accent inline-block text-sm font-semibold hover:underline"
              >
                Unduh hasil triase (PDF)
              </a>
            </CardBody>
          </Card>

          <Card>
            <CardBody className="text-content-muted flex items-start gap-2 text-xs">
              <ShieldAlert aria-hidden className="mt-px size-3.5 shrink-0" />
              <span>
                Hasil triase adalah perkiraan prioritas, bukan diagnosis. Bila keluhan memberat,
                segera ke IGD tanpa menunggu jadwal.
              </span>
            </CardBody>
          </Card>

          <div className="flex flex-wrap gap-2">
            <LinkButton to={serviceLink("/reservasi")} variant="primary" icon={<Stethoscope className="size-4" />}>
              Buat reservasi
            </LinkButton>
            <Button
              onClick={() => {
                setStage("complaint");
                setAnalysis(null);
                setChecked(new Set());
                setAnswers({});
                setManual([]);
                setComplaint("");
              }}
            >
              Triase baru
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
